import React, { useState, useEffect, useRef } from 'react';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';
import { PanelEmailData, useEmailPanel } from '../contexts/EmailPanelContext';
import { KTSVG } from '../../../../_metronic/helpers';
// useChat import removed 2026-05 — no longer needed after Chat-about-email retirement.
// StartChatFromEmailModal removed 2026-05 — Andrei is the chat surface; users open it from the header.
import { authFetch } from '../../../utils/cloudFunctionsAuth';
import sanitizeHtml from '../../../utils/sanitizeHtml';
import { getCachedGraphEmailBody, getOrFetchGraphEmailBody, persistGraphEmailBodyToFirestore } from '../utils/graphEmailBodyCache';
import {
  resolveInlineGraphImages,
  stripCidImages,
} from '../utils/inlineImageResolver';

// If your attachments array in PanelEmailData has a defined structure from Firestore:
interface AttachmentData {
  filename: string;
  url?: string; // For GCS stored attachments from original EmailViewModal logic
  content?: string; // For Base64 encoded attachments if sent from SendEmailForm
  contentType?: string;
  // Graph attachment list support
  id?: string;
  size?: number | null;
  isInline?: boolean;
  contentId?: string | null;
  // Add other relevant fields your attachment objects might have
}

// Functions like getStyledHtml can be kept if they are pure and don't rely on modal state.
const getStyledHtml = (content: string) => {
  const bodyBg = '#ffffff';
  const textColor = '#212529';

  return `
  <html>
    <head>
      <style>
        body {
          background-color: ${bodyBg};
          color: ${textColor};
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
          margin: 0;
          padding: 15px;
          overflow-wrap: break-word;
          word-wrap: break-word;
          word-break: break-word;
        }
        img {
          max-width: 100%;
          height: auto;
        }
      </style>
      <base target="_blank">
    </head>
    <body>
      ${content}
    </body>
  </html>`;
};

// Sanitise untrusted email HTML with DOMPurify (the shared util) rather than a
// hand-rolled regex — the regex missed non-script vectors (forms, meta-refresh,
// <base>, CSS exfil) that survive in a same-origin iframe. DOMPurify forbids
// script/iframe/object/embed/form/base/meta/link, strips handlers + js:/vbscript:
// URIs, and keeps cid: refs (resolved to data: AFTER this step) intact.
const sanitizeEmailHtml = (html: string): string => (html ? sanitizeHtml(html) : '');

// Escape plain-text bodies so text that happens to contain '<' / '&' renders as
// literal text, not markup (previously interpolated raw into the HTML view).
const escapeHtml = (s: string): string =>
  String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const textToHtml = (text: string): string => `<div style="white-space: pre-wrap;">${escapeHtml(text)}</div>`;

// Replace remote http(s) <img> sources with a 1px placeholder, stashing the real
// URL in data-blocked-src so a "load images" action can restore them. Inline
// cid:/data: images (already resolved) are left intact. Blocks tracking pixels +
// IP leakage from auto-loading remote content.
const REMOTE_IMG_PLACEHOLDER = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
const blockRemoteImages = (html: string): { html: string; blocked: number } => {
  let blocked = 0;
  const out = html.replace(/<img\b[^>]*>/gi, (tag) => {
    const m = tag.match(/\bsrc\s*=\s*["']([^"']+)["']/i);
    const src = m?.[1] || '';
    if (/^https?:\/\//i.test(src)) {
      blocked++;
      return tag
        .replace(/<img\b/i, `<img data-blocked-src="${src.replace(/"/g, '&quot;')}"`)
        .replace(/\bsrc\s*=\s*["'][^"']*["']/i, `src="${REMOTE_IMG_PLACEHOLDER}"`);
    }
    return tag;
  });
  return { html: out, blocked };
};

// Cid + inline-image helpers were lifted out to
// ../utils/inlineImageResolver so EmailView, TerminalEmailView, and the
// JobWorkspace thread pane share a single implementation and a
// process-wide attachment cache.

interface EmailViewProps {
  // Ensure the emailData passed to EmailView contains all necessary fields
  // including customerId and a well-defined attachments array.
  emailData: (PanelEmailData & { customerId?: string | null, attachments?: AttachmentData[] }) | null;
}

const EmailView: React.FC<EmailViewProps> = ({ emailData }) => {
  const [htmlContent, setHtmlContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isBodyLoading, setIsBodyLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bodyKind, setBodyKind] = useState<'html' | 'text' | null>(null);
  const [loadingFormatted, setLoadingFormatted] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState<string | null>(null); // State for copy feedback
  const [graphAttachments, setGraphAttachments] = useState<AttachmentData[] | null>(null);
  const [showAttSection, setShowAttSection] = useState(false);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [attachmentDownloadErrors, setAttachmentDownloadErrors] = useState<Record<string, string>>({});
  const [attachmentsError, setAttachmentsError] = useState<string | null>(null);
  // Remote-image (tracking-pixel) blocking — on by default; the user can load
  // images per-email via the banner. Inline/cid images always show.
  const [blockRemote, setBlockRemote] = useState(true);
  const [remoteBlockedCount, setRemoteBlockedCount] = useState(0);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const heightAdjustmentTimeoutRef = useRef<number | null>(null);
  const latestEmailKeyRef = useRef<string>('');
  
  // Get the email panel context to close the panel
  const { closeEmailPanel } = useEmailPanel();

  // Normalize legacy/variant shapes so opening an email doesn't regress to "Unknown/No Subject"
  const getDisplaySubject = (data: any): string => {
    const subject = data?.subject || data?.title || data?.emailSubject;
    return subject ? String(subject) : 'No Subject';
  };

  const getDisplayFromList = (data: any): { address: string; name?: string; text?: string }[] => {
    const fromVal = data?.from;
    if (Array.isArray(fromVal)) return fromVal;
    if (typeof fromVal === 'string') {
      const v = fromVal.trim();
      if (!v) return [];
      // If it's already an address, keep it as address; otherwise treat as name
      return v.includes('@') ? [{ address: v }] : [{ address: '', name: v }];
    }
    // Fallback: if we have a pre-rendered sender string, show it as a name
    const sender = typeof data?.sender === 'string' ? data.sender.trim() : '';
    if (sender) return [{ address: '', name: sender, text: sender }];
    return [];
  };

  // Remove theme detection useEffect for iframe, as it will always be styled for white background
  // useEffect(() => { ... });

  const adjustIframeHeight = React.useCallback(() => {
    // Clear any pending height adjustments to debounce
    if (heightAdjustmentTimeoutRef.current) {
      clearTimeout(heightAdjustmentTimeoutRef.current);
    }
    
    // Debounce the height adjustment to prevent ResizeObserver loops
    heightAdjustmentTimeoutRef.current = window.setTimeout(() => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        // Use requestAnimationFrame to sync with browser rendering cycle
        requestAnimationFrame(() => {
          if (iframeRef.current && iframeRef.current.contentWindow) {
            try {
              const height = iframeRef.current.contentWindow.document.body.scrollHeight;
              iframeRef.current.style.height = `${height + 30}px`; // Add some padding
            } catch (error) {
              // Silently handle cross-origin or other iframe access errors
              console.debug('Unable to adjust iframe height:', error);
            }
          }
        });
      }
    }, 250); // Debounce for 250ms to avoid conflicts with panel animations
  }, []);

  const handleCopyEmail = (email: string) => {
    navigator.clipboard.writeText(email).then(() => {
      setCopiedEmail(email);
      setTimeout(() => setCopiedEmail(null), 2000); // Hide "Copied!" after 2 seconds
    }).catch(err => {
      console.error('Failed to copy email: ', err);
      // Optionally, set an error message state here to inform the user
    });
  };

  // 'Chat about this Email' flow removed 2026-05. Andrei is the chat
  // surface; users open it from the header [chat]/[andrei] buttons.
  // closeEmailPanel is still imported because it's used elsewhere
  // in this component; just check usage further down.

  useEffect(() => {
    const processEmailContent = async () => {
      if (!emailData) {
        setLoading(false);
        setError('No email data provided.');
        return;
      }
      setLoading(true);
      setError(null);
      setHtmlContent(null);
      setBodyKind(null);
      try {
        const emailId = String((emailData as any)?.emailId || emailData.id || '');
        const emailKey = String((emailData as any)?.id || emailId || '');
        latestEmailKeyRef.current = emailKey;
        let html = emailData.html || null;
        let text = emailData.text || null;

        // For Graph emails we initially store bodyPreview only. Fetch full body on-demand when viewing.
        // Check both source='graph' and presence of graph.id (some older emails may have partial metadata)
        const isGraph = (emailData as any)?.source === 'graph';
        const hasGraphMetadata = !!(emailData as any)?.graph?.id;
        const canFetchFromGraph = isGraph || hasGraphMetadata;
        const hasFullBody = !!(html && html.length > 200) || !!(text && text.length > 300);
        
        if (canFetchFromGraph && !hasFullBody && emailId) {
          // 1) Instant path: use prefetched in-memory TEXT body if present
          const cachedText = getCachedGraphEmailBody(emailId, 'text');
          if (cachedText?.content) {
            text = cachedText.content || text;
          } else {
            // 2) Fast path: fetch TEXT body first (smaller payload; renders quickly)
            setIsBodyLoading(true);
            try {
              const body = await getOrFetchGraphEmailBody(emailId, 'text');
              if (body.contentType === 'text') text = body.content || text;
              else html = body.content || html;

              // 3) Persist for future opens (keep it light with size cap + async)
              void persistGraphEmailBodyToFirestore(emailId, body);
            } catch (fetchErr) {
              // Graph API failed - use whatever we have from Firestore
              console.warn('[EmailView] Failed to fetch body from Graph:', fetchErr);
            } finally {
              setIsBodyLoading(false);
            }
          }
        }

        let emailBodyHtml = html
          ? sanitizeEmailHtml(html)
          : (text ? textToHtml(text) : '<div>No content available for this email.</div>');

        // For Graph emails with HTML content, resolve inline cid: images to data URLs
        // This restores the pre-refactor behavior where inline images load automatically
        if ((isGraph || hasGraphMetadata) && html && emailId) {
          try {
            emailBodyHtml = await resolveInlineGraphImages(emailId, emailBodyHtml, { attachments: graphAttachments });
          } catch (imgErr) {
            console.warn('[EmailView] Failed to resolve inline images:', imgErr);
            // Continue with unresolved images - stripCidImages will clean them up
            emailBodyHtml = stripCidImages(emailBodyHtml);
          }
        }

        // Block remote (tracking) images unless the user has chosen to load them.
        if (html && blockRemote) {
          const r = blockRemoteImages(emailBodyHtml);
          emailBodyHtml = r.html;
          if (latestEmailKeyRef.current === emailKey) setRemoteBlockedCount(r.blocked);
        } else if (latestEmailKeyRef.current === emailKey) {
          setRemoteBlockedCount(0);
        }

        // Stale-async guard: if the user opened a DIFFERENT email while this
        // body was loading, don't clobber the newer email's content.
        if (latestEmailKeyRef.current !== emailKey) return;

        setBodyKind(html ? 'html' : (text ? 'text' : null));
        setHtmlContent(getStyledHtml(emailBodyHtml));
      } catch (e: any) {
        console.error('Error processing email content for view:', e);
        setError('Failed to load email content.');
        const fallbackText = emailData.text ? textToHtml(emailData.text) : '<div>Error loading email content.</div>';
        setBodyKind(emailData.html ? 'html' : (emailData.text ? 'text' : null));
        setHtmlContent(getStyledHtml(fallbackText));
      }
      setLoading(false);
    };
    processEmailContent();
  }, [emailData, blockRemote]);

  // Reset remote-image blocking to ON whenever a different email is opened.
  useEffect(() => {
    setBlockRemote(true);
    setRemoteBlockedCount(0);
  }, [emailData?.id]);

  const loadFormattedHtml = async () => {
    if (!emailData) return;
    const isGraph = (emailData as any)?.source === 'graph';
    if (!isGraph) return;
    const emailId = String((emailData as any)?.emailId || emailData.id || '');
    if (!emailId) return;

    setLoadingFormatted(true);
    try {
      const body = await getOrFetchGraphEmailBody(emailId, 'html');
      if (body.contentType === 'html' && body.content) {
        // Restore inline-image behavior: resolve `cid:` images to real data URLs when loading formatted view.
        const sanitized = sanitizeEmailHtml(body.content);
        let resolved = await resolveInlineGraphImages(emailId, sanitized, { attachments: graphAttachments });
        if (blockRemote) {
          const r = blockRemoteImages(resolved);
          resolved = r.html;
          setRemoteBlockedCount(r.blocked);
        }
        setBodyKind('html');
        setHtmlContent(getStyledHtml(resolved));
        void persistGraphEmailBodyToFirestore(emailId, body);
      }
    } catch (e) {
      console.error('Failed to load formatted HTML body', e);
    } finally {
      setLoadingFormatted(false);
    }
  };

  // Load Graph attachments list on demand when viewing a Graph email.
  // The view can receive PARTIAL email objects (search rows, timelines,
  // notification opens) that lack `hasAttachments`/`source` even when the doc
  // has them — which silently hid real attachments. When the flag is missing,
  // resolve it from the email doc itself instead of trusting the caller.
  useEffect(() => {
    const run = async () => {
      if (!emailData) return;
      const docId = String((emailData as any)?.id || '');
      const isGraph = (emailData as any)?.source === 'graph' || !!(emailData as any)?.graph?.id;
      let hasAtt: boolean | undefined =
        typeof (emailData as any)?.hasAttachments === 'boolean' ? (emailData as any).hasAttachments : undefined;
      if (isGraph && hasAtt === undefined && docId) {
        try {
          const snap = await getDoc(doc(db, 'emails', docId));
          hasAtt = snap.exists() ? (snap.data() as any)?.hasAttachments === true : false;
        } catch { hasAtt = false; }
      }
      if (!isGraph || hasAtt !== true) {
        setShowAttSection(false);
        setGraphAttachments(null);
        setAttachmentsError(null);
        return;
      }
      setShowAttSection(true);
      setAttachmentsLoading(true);
      setAttachmentsError(null);
      try {
        const resp = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/getGraphEmailAttachments', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emailId: emailData.id }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || data?.ok !== true) {
          const msg = data?.error || `Failed to load attachments (HTTP ${resp.status})`;
          const dbg = data?.debug ? ` Debug: ${JSON.stringify(data.debug)}` : '';
          throw new Error(msg + dbg);
        }
        const list = Array.isArray(data?.attachments) ? data.attachments : [];
        setGraphAttachments(
          list.map((a: any) => ({
            id: a.id,
            filename: a.name || 'attachment',
            contentType: a.contentType || 'application/octet-stream',
            size: typeof a.size === 'number' ? a.size : null,
            isInline: !!a.isInline,
            contentId: a.contentId || null,
          }))
        );
      } catch (e) {
        console.error('Failed to load graph attachments', e);
        setGraphAttachments([]);
        setAttachmentsError(e instanceof Error ? e.message : String(e));
      } finally {
        setAttachmentsLoading(false);
      }
    };
    run();
  }, [emailData?.id]);

  const downloadGraphAttachment = async (att: AttachmentData) => {
    if (!emailData?.id || !att.id) return;
    setDownloadingId(att.id);
    // Clear any previous error for this attachment
    setAttachmentDownloadErrors(prev => {
      if (!prev[att.id!]) return prev;
      const next = { ...prev };
      delete next[att.id!];
      return next;
    });
    try {
      const resp = await authFetch('https://us-central1-web-construct-app.cloudfunctions.net/downloadGraphAttachment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailId: emailData.id, attachmentId: att.id }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data?.error || `Download failed (HTTP ${resp.status})`);
      }
      const blob = await resp.blob();

      // Try to get filename from headers. Prefer RFC 5987 `filename*` (UTF-8,
      // percent-encoded) for non-ASCII names; fall back to plain `filename=`.
      const cd = resp.headers.get('content-disposition') || '';
      let filename = att.filename || 'attachment';
      const ext = cd.match(/filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i);
      const plain = cd.match(/filename\s*=\s*"?([^";]+)"?/i);
      if (ext?.[1]) {
        try { filename = decodeURIComponent(ext[1].trim().replace(/^["']|["']$/g, '')); } catch { /* keep fallback */ }
      } else if (plain?.[1]) {
        filename = plain[1].trim();
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Attachment download failed', e);
      const msg = e instanceof Error ? e.message : 'Download failed';
      setAttachmentDownloadErrors(prev => ({ ...prev, [att.id!]: msg }));
    } finally {
      setDownloadingId(null);
    }
  };

  // Cleanup height adjustment timeout on unmount
  useEffect(() => {
    return () => {
      if (heightAdjustmentTimeoutRef.current) {
        clearTimeout(heightAdjustmentTimeoutRef.current);
      }
    };
  }, []);

  const formatDate = (timestampInput: any) => {
    if (!timestampInput) return 'N/A';
    const date = timestampInput.toDate ? timestampInput.toDate() : new Date(timestampInput);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (!emailData) {
    return <div className="alert alert-danger m-5">{error || 'Could not load email data.'}</div>;
  }

  const displaySubject = getDisplaySubject(emailData);
  const fromList = getDisplayFromList(emailData);
  
  // Helper to format address fields that might be strings, arrays of strings, or arrays of objects
  const formatAddresses = (field: any): string => {
    if (!field) return '';
    if (Array.isArray(field)) {
      return field
        .map((item: any) => (typeof item === 'string' ? item : item?.address || item?.text || ''))
        .filter(Boolean)
        .join(', ');
    }
    if (typeof field === 'string') return field;
    if (typeof field === 'object') return field?.address || field?.text || '';
    return '';
  };
  
  const displayTo = formatAddresses(emailData.to);
  const displayCc = formatAddresses(emailData.cc);
  const displayBcc = formatAddresses(emailData.bcc);
  const isGraph = (emailData as any)?.source === 'graph' || !!(emailData as any)?.graph?.id;
  const canLoadFormatted = isGraph && (bodyKind === 'text' || bodyKind === null) && !loading && !loadingFormatted;

  return (
    <div className="p-4">
      {/* Email Header Info */}
      <div className="mb-4 pb-4 border-bottom">
        <h4 className="fw-bold mb-1">{displaySubject}</h4>
        {emailData.customerId && (
          <div className="text-muted fs-7 mb-2">
            Linked Client ID: {emailData.customerId}
          </div>
        )}
        <div className="d-flex flex-column text-muted fs-7">
          <div className="d-flex align-items-center mb-1">
            <strong className="me-2" style={{ minWidth: '50px' }}>From:</strong>
            <span>
              {fromList.map((f, index) => (
                <React.Fragment key={index}>
                  {f.name && f.address ? `${f.name} <` : ''}
                  {f.address ? (
                    <span 
                      title="Copy email address"
                      style={{cursor: 'pointer'}}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyEmail(f.address);
                      }}
                    >
                      {f.address}
                    </span>
                  ) : (
                    f.name
                  )}
                  {f.name && f.address ? `>` : ''}
                  {copiedEmail === f.address && <span className="ms-1 text-success fs-8 fw-semibold">Copied!</span>}
                  {index < fromList.length - 1 && ', '}
                </React.Fragment>
              )) || 'N/A'}
            </span>
          </div>
          <div className="d-flex align-items-center mb-1">
            <strong className="me-2" style={{ minWidth: '50px' }}>To:</strong>
            <span>{displayTo || 'N/A'}</span>
          </div>
          {displayCc && (
            <div className="d-flex align-items-center mb-1">
              <strong className="me-2" style={{ minWidth: '50px' }}>CC:</strong>
              <span>{displayCc}</span>
            </div>
          )}
          {displayBcc && (
            <div className="d-flex align-items-center mb-1">
              <strong className="me-2" style={{ minWidth: '50px' }}>BCC:</strong>
              <span>{displayBcc}</span>
            </div>
          )}
          <div className="d-flex align-items-center">
            <strong className="me-2" style={{ minWidth: '50px' }}>Date:</strong>
            <span>{formatDate(emailData.date)}</span>
          </div>
        </div>

        {/* Progressive load affordances */}
        {(loading || loadingFormatted) && (
          <div className="mt-3 d-flex align-items-center text-muted fs-7">
            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Loading email content…
          </div>
        )}
        {error && (
          <div className="alert alert-danger mt-3 mb-0">{error}</div>
        )}
      </div>

      {/* Sticky "Load formatted view" toggle — always visible while scrolling */}
      {canLoadFormatted && (
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: 'var(--bs-body-bg)',
            borderBottom: '1px solid var(--bs-border-color)',
            padding: '6px 16px',
          }}
        >
          <button type="button" className="btn btn-sm btn-light-primary" onClick={loadFormattedHtml} disabled={loadingFormatted}>
            {loadingFormatted ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Loading...
              </>
            ) : (
              'Load formatted view'
            )}
          </button>
          <span className="ms-2 text-muted fs-8">Fast mode is showing plain text for speed.</span>
        </div>
      )}

      {/* "Chat about this Email" button removed 2026-05. Open Andrei
          via the header [andrei] button instead. */}

      {/* Attachments */}
      {(emailData.attachments && emailData.attachments.length > 0) && (
        <div className="mb-4 pb-4 border-bottom">
          <h6 className="fw-bold mb-2 fs-6">Attachments:</h6>
          <ul className="list-unstyled">
            {emailData.attachments.map((att: AttachmentData, index: number) => (
              <li key={index} className="mb-1">
                <a 
                  href={att.url || '#'} // Prefer URL if available (GCS links)
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="d-inline-flex align-items-center text-primary text-hover-primary fs-7"
                  // If it's a base64 attachment from a sent item, it won't be directly downloadable via URL.
                  // This UI primarily supports GCS URLs for received email attachments.
                  title={!att.url ? "Attachment content not directly downloadable from here" : att.filename}
                  onClick={(e) => { if (!att.url) e.preventDefault(); }} // Prevent click if no URL
                >
                  <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-5 me-2' />
                  {att.filename || 'Unnamed File'}
                  {att.contentType && <span className="text-muted ms-2 fs-8">({att.contentType})</span>}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Graph Attachments (on-demand) */}
      {showAttSection && (
        <div className="mb-4 pb-4 border-bottom">
          <h6 className="fw-bold mb-2 fs-6">Attachments:</h6>
          {attachmentsLoading && (
            <div className="text-muted fs-7">Loading attachments…</div>
          )}
          {!attachmentsLoading && attachmentsError && (
            <div className="alert alert-warning py-2 px-3 mb-2">
              <div className="fw-semibold">Couldn’t load attachments</div>
              <div className="text-muted fs-7" style={{ whiteSpace: 'pre-wrap' }}>{attachmentsError}</div>
            </div>
          )}
          {!attachmentsLoading && graphAttachments && graphAttachments.length === 0 && (
            <div className="text-muted fs-7">No downloadable attachments.</div>
          )}
          {!attachmentsLoading && graphAttachments && graphAttachments.length > 0 && (
            <ul className="list-unstyled">
              {graphAttachments.map((att, idx) => (
                <li key={att.id || idx} className="mb-2">
                  <button
                    type="button"
                    className="btn btn-link p-0 d-inline-flex align-items-center text-primary text-hover-primary fs-7"
                    onClick={() => downloadGraphAttachment(att)}
                    disabled={!att.id || downloadingId === att.id}
                    title={att.filename}
                  >
                    <KTSVG path='/media/icons/duotune/files/fil003.svg' className='svg-icon-5 me-2' />
                    {att.filename || 'Unnamed File'}
                    {att.contentType && <span className="text-muted ms-2 fs-8">({att.contentType})</span>}
                    {typeof att.size === 'number' && <span className="text-muted ms-2 fs-8">{Math.round(att.size / 1024)} KB</span>}
                    {downloadingId === att.id && (
                      <span className="ms-2 spinner-border spinner-border-sm" role="status" aria-label="Downloading"></span>
                    )}
                  </button>
                  {att.id && attachmentDownloadErrors[att.id] && (
                    <div className="text-danger fs-8 mt-1">
                      <i className="bi bi-exclamation-circle me-1"></i>
                      {attachmentDownloadErrors[att.id]}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Remote-content (tracking-image) blocked banner */}
      {blockRemote && remoteBlockedCount > 0 && (
        <div className="alert alert-warning d-flex align-items-center justify-content-between py-2 px-3 mb-2" role="status">
          <span className="fs-7">
            <i className="bi bi-shield-lock me-1"></i>
            {remoteBlockedCount} remote image{remoteBlockedCount === 1 ? '' : 's'} blocked to protect your privacy (senders can use them to track opens).
          </span>
          <button type="button" className="btn btn-sm btn-light-warning" onClick={() => setBlockRemote(false)}>
            Load images
          </button>
        </div>
      )}

      {/* Email Body via IFrame */}
      {htmlContent ? (
        // Frame the (intentionally white) email body as a "paper" card so it
        // reads as deliberate against graphite's warm surface, not a raw seam.
        <div style={{ background: '#ffffff', border: '0.5px solid var(--bs-border-color)', borderRadius: 'var(--bs-border-radius, 0.65rem)', overflow: 'hidden' }}>
          <iframe
            ref={iframeRef}
            srcDoc={htmlContent}
            style={{
              width: '100%',
              minHeight: '400px', // Set a min-height for initial rendering
              border: 'none',
              display: 'block', // To prevent any extra space below the iframe
            }}
            sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
            title="Email Content"
            onLoad={adjustIframeHeight}
          />
        </div>
      ) : (
        <div className='p-4'>
          {isBodyLoading ? (
            <>
              {/* Skeleton shown while fetching Graph body from the API */}
              <div className="mb-3">
                <div className="placeholder-glow">
                  <span className="placeholder col-12 mb-2" style={{ display: 'block', height: '14px', borderRadius: '4px' }}></span>
                  <span className="placeholder col-10 mb-2" style={{ display: 'block', height: '14px', borderRadius: '4px' }}></span>
                  <span className="placeholder col-11 mb-2" style={{ display: 'block', height: '14px', borderRadius: '4px' }}></span>
                  <span className="placeholder col-8" style={{ display: 'block', height: '14px', borderRadius: '4px' }}></span>
                </div>
              </div>
              <div className="mb-3">
                <div className="placeholder-glow">
                  <span className="placeholder col-10 mb-2" style={{ display: 'block', height: '14px', borderRadius: '4px' }}></span>
                  <span className="placeholder col-9" style={{ display: 'block', height: '14px', borderRadius: '4px' }}></span>
                </div>
              </div>
            </>
          ) : (loading || loadingFormatted) ? (
            <>
              {/* Lightweight skeleton + show preview text immediately */}
              <div className="mb-3">
                <div className="placeholder-glow">
                  <span className="placeholder col-10 me-2"></span>
                  <span className="placeholder col-6"></span>
                </div>
              </div>
              <div className="mb-3">
                <div className="placeholder-glow">
                  <span className="placeholder col-9 me-2"></span>
                  <span className="placeholder col-7"></span>
                </div>
              </div>
              <div className="text-muted fs-7" style={{ whiteSpace: 'pre-wrap' }}>
                {(emailData?.text || '').trim() ? emailData.text : 'Loading…'}
              </div>
            </>
          ) : (
            <div className='text-muted'>No content available.</div>
          )}
        </div>
      )}

    </div>
  );
};

export default EmailView; 